import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { runService, webSocketService } from '../services';
import { RunWithDetails, Grade, RunDisplayInfo, RunAction, RunInstance } from '../types';
import { LoadingSpinner, Card, Badge, Button, Alert, Progress } from '../components/ui';
import { TerminalComponent } from '../components/Terminal';
import { EnhancedInstructions } from '../components/EnhancedInstructions';
import ReactMarkdown from 'react-markdown';

export function RunDetail() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  
  const [run, setRun] = useState<RunWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [grade, setGrade] = useState<Grade | null>(null);
  const [instances, setInstances] = useState<RunInstance[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDetailedReport, setShowDetailedReport] = useState(false);

  const runIdNum = runId ? parseInt(runId, 10) : null;

  const loadRun = useCallback(async () => {
    if (!runIdNum) return;
    
    try {
      const runData = await runService.getRun(runIdNum);
      setRun(runData);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load run:', err);
      if (err.response?.status === 404) {
        setError('Lab run not found.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You can only view your own lab runs.');
      } else {
        setError('Failed to load lab run. Please try again.');
      }
    }
  }, [runIdNum]);

  const loadGrade = useCallback(async () => {
    if (!runIdNum) return;
    
    try {
      const gradeData = await runService.getGradeReport(runIdNum);
      setGrade(gradeData);
    } catch (err: any) {
      // Grade might not exist yet, that's ok
      if (err.response?.status !== 404) {
        console.error('Failed to load grade:', err);
      }
    }
  }, [runIdNum]);

  const loadInstances = useCallback(async () => {
    if (!runIdNum) return;
    
    try {
      const instanceData = await runService.getRunInstances(runIdNum);
      setInstances(instanceData);
    } catch (err: any) {
      console.error('Failed to load instances:', err);
      // Set empty array as fallback
      setInstances([]);
    }
  }, [runIdNum]);

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await loadRun();
      await loadGrade();
      await loadInstances();
      setLoading(false);
    };

    initializeData();
  }, [loadRun, loadGrade, loadInstances]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!runIdNum) return;

    const token = localStorage.getItem('auth_token');
    if (token && !wsConnected) {
      webSocketService.connect(token);
      setWsConnected(true);
    }

    // Subscribe to run state changes
    const unsubscribeStateChange = webSocketService.onRunStateChanged((message) => {
      if (message.payload.run_id === runIdNum) {
        loadRun(); // Reload run data when state changes
        loadInstances(); // Reload instances when state changes
      }
    });

    // Subscribe to grading completion
    const unsubscribeGradeComplete = webSocketService.onGradeCompleted((message) => {
      if (message.payload.run_id === runIdNum) {
        loadGrade(); // Reload grade when grading completes
      }
    });

    // Subscribe to TTL extensions
    const unsubscribeTTLExtend = webSocketService.onTTLExtended((message) => {
      if (message.payload.run_id === runIdNum) {
        loadRun(); // Reload run data when TTL is extended
      }
    });

    return () => {
      unsubscribeStateChange();
      unsubscribeGradeComplete();
      unsubscribeTTLExtend();
    };
  }, [runIdNum, wsConnected, loadRun, loadGrade]);

  const handleAction = async (action: RunAction) => {
    if (!run) return;

    setActionLoading(prev => ({ ...prev, [action.type]: true }));
    
    try {
      switch (action.type) {
        case 'grade':
          await runService.gradeRun(run.id);
          
          // Show success notification
          setSuccessMessage('Grading started! Results will appear shortly.');
          
          // Add small delay to ensure loading state is visible
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Clear success message after delay
          setTimeout(() => {
            setSuccessMessage(null);
          }, 4000);
          
          // Reload grade data after a short delay (grading takes time)
          setTimeout(() => {
            loadGrade();
          }, 3000);
          
          break;
          
        case 'extend':
          await runService.extendTTL(run.id, { hours: 6 });
          await loadRun();
          break;
          
        case 'destroy':
          if (window.confirm('Are you sure you want to destroy this lab? This action cannot be undone.')) {
            await runService.destroyRun(run.id);
            await loadRun();
          }
          break;
          
        case 'view-report':
          setShowDetailedReport(!showDetailedReport);
          break;
          
        default:
          console.warn('Unhandled action type:', action.type);
      }
    } catch (err: any) {
      console.error('Action failed:', err);
      let errorMessage = `Failed to ${action.label.toLowerCase()}. Please try again.`;
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [action.type]: false }));
    }
  };

  const getRunDisplayInfo = (run: RunWithDetails): RunDisplayInfo => {
    const statusColor = runService.getStatusColor(run.state);
    const statusText = runService.getStatusText(run.state);
    const timeRemaining = runService.formatTimeRemaining(run.ttl_expires_at);
    
    const actions: RunAction[] = [];
    
    if (runService.canSSH(run.state) && run.ip_address) {
      actions.push({
        type: 'ssh',
        label: 'SSH Access',
        disabled: false,
        tooltip: `Connect via: ${runService.getSSHCommand(run)}`
      });
    }
    
    if (runService.canGrade(run.state)) {
      actions.push({
        type: 'grade',
        label: 'Grade Lab',
        disabled: false,
        loading: actionLoading.grade
      });
    }
    
    if (runService.canExtendTTL(run.state)) {
      actions.push({
        type: 'extend',
        label: 'Extend (+6h)',
        disabled: false,
        loading: actionLoading.extend
      });
    }
    
    if (runService.canDestroy(run.state)) {
      actions.push({
        type: 'destroy',
        label: 'Destroy Lab', // Changed from 'Stop Lab'
        disabled: false,
        loading: actionLoading.destroy
      });
    }
    
    if (grade) {
      actions.push({
        type: 'view-report',
        label: showDetailedReport ? 'Hide Report' : 'View Report',
        disabled: false
      });
    }

    return {
      run,
      statusText,
      statusColor,
      actions,
      timeRemaining,
      progressText: getProgressText(run.state)
    };
  };

  const getProgressText = (state: string): string => {
    switch (state) {
      case 'pending':
        return 'Lab is queued for provisioning...';
      case 'provisioning':
        return 'Setting up virtual machine...';
      case 'active':
        return 'Lab is ready! You can connect via SSH.';
      case 'destroying':
        return 'Cleaning up resources...';
      case 'destroyed':
        return 'Lab has been destroyed.'; // Changed from 'Lab has been stopped.'
      case 'failed':
        return 'Lab provisioning failed. Please try again.';
      default:
        return '';
    }
  };

  const getProgressPercentage = (state: string): number => {
    switch (state) {
      case 'pending':
        return 10;
      case 'provisioning':
        return 50;
      case 'active':
        return 100;
      case 'destroying':
        return 75;
      case 'destroyed':
        return 100;
      case 'failed':
        return 100;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="flex items-center justify-center mt-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <Alert variant="error" className="mb-6">
            {error || 'Lab run not found'}
            <Button 
              onClick={() => navigate('/labs')} 
              className="ml-4"
              size="sm"
            >
              Back to Labs
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  const displayInfo = getRunDisplayInfo(run);

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header with back link */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/labs')} 
            className="text-blue-400 hover:text-blue-300 mb-4 inline-flex items-center"
          >
            ← Back to Labs Catalog
          </button>
        </div>

        {/* Lab Title */}
        <div className="mb-4">
          <h1 className="text-4xl font-bold mb-2">{run.lab.title}</h1>
          <div className="flex items-center gap-4 text-gray-400">
            <span>Run #{run.id}</span>
            <Badge className={`
              ${run.lab.difficulty === 'beginner' ? 'bg-green-500' :
                run.lab.difficulty === 'intermediate' ? 'bg-yellow-500' :
                'bg-red-500'
              } text-white
            `}>
              {run.lab.difficulty}
            </Badge>
          </div>
        </div>
          
        {/* Success Message */}
        {successMessage && (
          <Alert variant="success" className="mb-4">
            {successMessage}
          </Alert>
        )}
        
        {/* Progress Bar between title and instructions */}
        {(run.state === 'provisioning' || run.state === 'pending') && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Badge className={`
                ${displayInfo.statusColor === 'success' ? 'bg-green-500' : ''}
                ${displayInfo.statusColor === 'processing' ? 'bg-blue-500' : ''}
                ${displayInfo.statusColor === 'error' ? 'bg-red-500' : ''}
                ${displayInfo.statusColor === 'warning' ? 'bg-yellow-500' : ''}
                ${displayInfo.statusColor === 'pending' ? 'bg-gray-500' : ''}
                text-white
              `}>
                {displayInfo.statusText}
              </Badge>
              {displayInfo.timeRemaining && (
                <span className="text-sm text-gray-400">TTL: {displayInfo.timeRemaining}</span>
              )}
            </div>
            <Progress 
              value={getProgressPercentage(run.state)} 
              className="mb-2"
            />
            <p className="text-sm text-gray-400">{displayInfo.progressText}</p>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Instructions */}
          <div className="lg:col-span-2">
            {(run.lab.slug === 'linux-file-permissions-users' || run.lab.slug === 'broken-web-server-rescue' || run.lab.slug === 'service-startup-failures' || run.lab.slug === 'django-application-disaster' || run.lab.slug === 'nextjs-fullstack' || run.lab.slug === 'wordpress-basics' || run.lab.slug === 'wordpress-database-connection-failure' || run.lab.slug === 'memory-management-mayhem' || run.lab.slug === 'react-node-stack-breakdown' || run.lab.slug === 'database-replication' || run.lab.slug === 'k3s-mini-cluster' || run.lab.slug === 'storage-migration-scaling' || run.lab.slug === 'backup-disaster-recovery' || run.lab.slug === 'basic-shell-scripting' || run.lab.slug === 'nginx-setup' || run.lab.slug === 'spaces-backup-automation') ? (
              <EnhancedInstructions 
                labSlug={run.lab.slug}
                instructions={run.lab.manifest?.instructions}
              />
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <div className="p-6">
                  <h2 className="text-2xl font-semibold mb-4">Instructions</h2>
                  <div className="prose prose-invert max-w-none">
                    {run.lab.manifest?.instructions ? (
                      <ReactMarkdown
                        components={{
                          h1: ({children}: any) => <h1 className="text-3xl font-bold mb-4 text-white">{children}</h1>,
                          h2: ({children}: any) => <h2 className="text-2xl font-semibold mb-3 mt-6 text-white">{children}</h2>,
                          h3: ({children}: any) => <h3 className="text-xl font-medium mb-2 mt-4 text-white">{children}</h3>,
                          p: ({children}: any) => <p className="mb-4 text-gray-300 leading-relaxed">{children}</p>,
                          ul: ({children}: any) => <ul className="list-disc list-inside mb-4 text-gray-300">{children}</ul>,
                          ol: ({children}: any) => <ol className="list-decimal list-inside mb-4 text-gray-300">{children}</ol>,
                          li: ({children}: any) => <li className="mb-1">{children}</li>,
                          code: ({inline, children}: any) => 
                            inline ? (
                              <code className="bg-gray-700 px-1 py-0.5 rounded text-blue-300">{children}</code>
                            ) : (
                              <code className="block bg-gray-900 p-3 rounded overflow-x-auto text-blue-300">{children}</code>
                            ),
                          pre: ({children}: any) => <pre className="bg-gray-900 p-3 rounded overflow-x-auto mb-4">{children}</pre>,
                          blockquote: ({children}: any) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-400">{children}</blockquote>,
                        }}
                      >
                        {run.lab.manifest.instructions}
                      </ReactMarkdown>
                    ) : (
                      <p className="text-gray-400">No instructions available for this lab.</p>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column: Actions and SSH Details */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-4">
              {/* Action Buttons */}
              {run.state === 'active' && (
                <div className="space-y-3">
                  {/* Grade Button */}
                  {displayInfo.actions.find(a => a.type === 'grade') && (
                    <Button
                      onClick={() => handleAction(displayInfo.actions.find(a => a.type === 'grade')!)}
                      disabled={actionLoading.grade}
                      className="w-full bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      size="lg"
                    >
                      {actionLoading.grade ? (
                        <div className="flex items-center justify-center space-x-2">
                          <LoadingSpinner size="sm" />
                          <span>Grading...</span>
                        </div>
                      ) : (
                        'Grade Lab'
                      )}
                    </Button>
                  )}

                  {/* Extend Button */}
                  {displayInfo.actions.find(a => a.type === 'extend') && (
                    <Button
                      onClick={() => handleAction(displayInfo.actions.find(a => a.type === 'extend')!)}
                      disabled={actionLoading.extend}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      size="lg"
                    >
                      {actionLoading.extend ? (
                        <div className="flex items-center justify-center space-x-2">
                          <LoadingSpinner size="sm" />
                          <span>Extending...</span>
                        </div>
                      ) : (
                        'Extend (+6h)'
                      )}
                    </Button>
                  )}

                  {/* Destroy Button */}
                  {displayInfo.actions.find(a => a.type === 'destroy') && (
                    <Button
                      onClick={() => handleAction(displayInfo.actions.find(a => a.type === 'destroy')!)}
                      disabled={actionLoading.destroy}
                      className="w-full bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      size="lg"
                    >
                      {actionLoading.destroy ? (
                        <div className="flex items-center justify-center space-x-2">
                          <LoadingSpinner size="sm" />
                          <span>Destroying...</span>
                        </div>
                      ) : (
                        'Destroy Lab'
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* SSH Connection Details */}
              {run.state === 'active' && instances.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-3">
                      {instances.length > 1 ? 'SSH Connections' : 'SSH Connection'}
                    </h3>
                    <div className="space-y-4">
                      {instances.map((instance) => (
                        <div key={instance.id} className="border-b border-gray-700 pb-3 last:border-b-0 last:pb-0">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-blue-300 capitalize">
                              {instance.name}
                            </span>
                            {instance.is_primary && (
                              <Badge className="bg-green-600 text-white text-xs">Primary</Badge>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div>
                              <span className="text-gray-400 text-xs block mb-1">IP Address:</span>
                              <code className="bg-gray-900 px-2 py-1 rounded text-blue-300 text-sm block">
                                {instance.public_ip || 'N/A'}
                              </code>
                            </div>
                            <div>
                              <span className="text-gray-400 text-xs block mb-1">SSH Command:</span>
                              <code className="bg-gray-900 px-2 py-1 rounded text-blue-300 text-sm block break-all">
                                {instance.ssh_command || 'N/A'}
                              </code>
                            </div>
                            {instance.role && (
                              <div>
                                <span className="text-gray-400 text-xs block mb-1">Role:</span>
                                <span className="text-gray-300 text-sm">{instance.role}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="pt-2 border-t border-gray-700">
                        <span className="text-gray-400 text-sm block mb-1">Time Remaining:</span>
                        <span className="text-white">
                          {displayInfo.timeRemaining || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Fallback SSH Connection for backward compatibility */}
              {run.state === 'active' && instances.length === 0 && run.ip_address && (
                <Card className="bg-gray-800 border-gray-700">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-3">SSH Connection</h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-gray-400 text-sm block mb-1">IP Address:</span>
                        <code className="bg-gray-900 px-2 py-1 rounded text-blue-300 text-sm block">
                          {run.ip_address}
                        </code>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm block mb-1">SSH Command:</span>
                        <code className="bg-gray-900 px-2 py-1 rounded text-blue-300 text-sm block break-all">
                          {runService.getSSHCommand(run)}
                        </code>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm block mb-1">Time Remaining:</span>
                        <span className="text-white">
                          {displayInfo.timeRemaining || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Grade Report */}
              {grade && (
                <Card className="bg-gray-800 border-gray-700">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-3">Grade Report</h3>
                    <div className="space-y-3">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-white">
                          {Math.round((grade.score / grade.total_points) * 100)}%
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          {grade.score}/{grade.total_points} points
                        </div>
                      </div>
                      {grade.report.summary && (
                        <div className="text-sm text-gray-400 text-center">
                          {grade.report.summary.passed_checks}/{grade.report.summary.total_checks} checks passed
                        </div>
                      )}
                      <div className="text-xs text-gray-500 text-center">
                        Graded: {new Date(grade.created_at).toLocaleTimeString()}
                      </div>
                      <Button
                        onClick={() => navigate(`/runs/${run.id}/grade`)}
                        className="w-full bg-gray-700 hover:bg-gray-600 text-white"
                        size="sm"
                      >
                        View Detailed Report →
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Status when not active */}
              {run.state !== 'active' && (
                <Card className="bg-gray-800 border-gray-700">
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-3">Status</h3>
                    <Badge className={`
                      ${displayInfo.statusColor === 'success' ? 'bg-green-500' : ''}
                      ${displayInfo.statusColor === 'processing' ? 'bg-blue-500' : ''}
                      ${displayInfo.statusColor === 'error' ? 'bg-red-500' : ''}
                      ${displayInfo.statusColor === 'warning' ? 'bg-yellow-500' : ''}
                      ${displayInfo.statusColor === 'pending' ? 'bg-gray-500' : ''}
                      text-white
                    `}>
                      {displayInfo.statusText}
                    </Badge>
                    <p className="text-sm text-gray-400 mt-2">{displayInfo.progressText}</p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
